import store from "../../store/configureStore";
import { updateSmartLoader } from "../../actions/app/global";
import { setCreativeApiError, editCreative } from "../../actions/app/creatives";
import { makeAuthHeaders } from "../oauth/auth";
import axios from "axios";
import _ from "lodash";
import { fetchSizeService } from "./size";

const initialLoaderNew = {
  loading: true,
  title: "Creative submission in progress...",
  steps: [
    {
      title: "Uploading assets",
      inProgress: true,
      completed: false
    },
    {
      title: "Resolving conflicting creatives",
      inProgress: false,
      completed: false
    },
    {
      title: "Inserting creatives",
      inProgress: false,
      completed: false
    },
    {
      title: "Building creative assignments",
      inProgress: false,
      completed: false
    },
    {
      title: "Finalizing process",
      inProgress: false,
      completed: false
    }
  ]
};

const initialLoaderDefaultDimensions = {
  loading: true,
  title: "Default submission in progress...",
  steps: [
    {
      title: "Uploading asset",
      inProgress: true,
      completed: false
    },
    {
      title: "Inserting creative",
      inProgress: false,
      completed: false
    },
    {
      title: "Building default assignments",
      inProgress: false,
      completed: false
    }
  ]
};

const initialLoaderNoAssignment = {
  loading: true,
  title: "Creative submission in progress...",
  steps: [
    {
      title: "Uploading assets",
      inProgress: true,
      completed: false
    },
    {
      title: "Finalizing process",
      inProgress: false,
      completed: false
    }
  ]
};

const initialLoaderExisting = {
  loading: true,
  title: "Creative submission in progress...",
  steps: [
    {
      title: "Building creative assignments",
      inProgress: false,
      completed: false
    },
    {
      title: "Resolving conflicting creatives",
      inProgress: false,
      completed: false
    },
    {
      title: "Finalizing process",
      inProgress: false,
      completed: false
    }
  ]
};

const reorderCreatives = (apiCreatives, creativeOrder) => {
  /**
   * @param apiCreatives - array of creatives sent from API
   * @param creativeOrder - array of creative UI ID's that denote the order of creatives
   *
   * @returns an array of the newly ordered creatives adhearing to what was shown in the tool
   */

  let orderedCreatives = [];

  creativeOrder.map((id) => {
    let foundCreative = apiCreatives.find((c) => c.metadata.id === id);
    if (foundCreative) orderedCreatives.push(foundCreative);
  });

  return orderedCreatives;
};

// Name to write into the Bart export. Existing GCM creatives are never named
// in Bart (their taxonomy is deliberately left empty — see the "no naming
// required" picker flow), so reading taxonomy.name alone produced a workbook
// of blank rows. Fall back to the DCM creative's own name, then the filename.
const getExportName = (creative) =>
  (creative.taxonomy && creative.taxonomy.name) ||
  (creative.dcmCreative && creative.dcmCreative.name) ||
  (creative.file && creative.file.name) ||
  "";

const makeAdInfo = () => {
  let creativesState = store.getState().creatives;

  return {
    rotationType: creativesState.rotationType,
    weightDistribution: creativesState.weightDistribution,
    hardCutOff: creativesState.hardCutoff,
    startTime: creativesState.startDate.toJSON(),
    endTime: creativesState.endDate.toJSON(),
    dynamicTargetingKeys: creativesState.dynamicTargetingKeys.map(
      (key) => key.name
    )
  };
};

const POOL_SIZE = 3;
const MAX_UPLOAD_ATTEMPTS = 3;
const UPLOAD_BASE_DELAY_MS = 2000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableUploadError = (error) => {
  // No response object -> network/connection failure; treat as transient.
  if (!error.response) return true;
  const status = error.response.status;
  return status === 408 || status === 429 || status >= 500;
};

// Index-keyed concurrency pool. results[i] aligns with tasks[i]; workers stop
// picking up new tasks once shouldAbort() returns true (in-flight tasks finish).
const runPool = async (tasks, limit, shouldAbort) => {
  const results = new Array(tasks.length);
  let next = 0;

  const worker = async () => {
    while (next < tasks.length) {
      if (shouldAbort && shouldAbort()) return;
      const i = next++;
      try {
        results[i] = { ok: true, value: await tasks[i]() };
      } catch (error) {
        results[i] = { ok: false, error };
      }
    }
  };

  const workerCount = Math.min(limit, tasks.length);
  await Promise.all(Array.from({ length: workerCount }, worker));

  return results;
};

// Uploads one file to /api/dcm/creativeAsset with bounded retries. axios throws
// on non-2xx, so the 401 / retry decisions live in the catch.
const uploadAssetWithRetry = async (creative, advertiserId, onProgress) => {
  let lastError;

  for (let attempt = 0; attempt < MAX_UPLOAD_ATTEMPTS; attempt++) {
    // A File/FormData body is single-use, so rebuild it for every attempt.
    let formData = new FormData();
    formData.set("advertiserId", advertiserId);
    formData.append(creative.taxonomy.name, creative.file);
    formData.append("names", creative.taxonomy.name);

    let headers = await makeAuthHeaders();

    try {
      let response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/dcm/creativeAsset`,
        formData,
        {
          headers,
          onUploadProgress: function (progressEvent) {
            if (onProgress) {
              onProgress(
                progressEvent.loaded,
                progressEvent.total || (creative.file && creative.file.size) || 0
              );
            }
          }
        }
      );

      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        throw new Error("UNAUTHORIZED");
      }

      lastError = error;

      if (
        !isRetryableUploadError(error) ||
        attempt === MAX_UPLOAD_ATTEMPTS - 1
      ) {
        break;
      }

      await sleep(
        UPLOAD_BASE_DELAY_MS * 2 ** attempt + Math.floor(Math.random() * 500)
      );
    }
  }

  let serverMessage =
    lastError &&
    lastError.response &&
    lastError.response.data &&
    lastError.response.data.message;

  throw new Error(
    serverMessage || (lastError && lastError.message) || "Upload failed"
  );
};

const postAssets = async (loader, creatives, advertiserId) => {
  if (!creatives.length) {
    loader.steps[0].inProgress = false;
    loader.steps[0].completed = true;
    store.dispatch(updateSmartLoader({ ...loader }));
    return [];
  }

  let loadedBytes = new Array(creatives.length).fill(0);
  let totalBytes = creatives.map((c) => (c.file && c.file.size) || 0);
  let doneCount = 0;
  let authFailed = false;

  let lastPercent = -1;
  let lastDone = -1;
  const dispatchProgress = () => {
    let loadedAll = loadedBytes.reduce((a, b) => a + b, 0);
    let totalAll = totalBytes.reduce((a, b) => a + b, 0);
    let percent = totalAll ? Math.round((loadedAll * 100) / totalAll) : 0;

    // Throttle re-renders: only dispatch on a visible change.
    if (percent === lastPercent && doneCount === lastDone) return;
    lastPercent = percent;
    lastDone = doneCount;

    loader.steps[0].title = `Uploading assets (${doneCount}/${creatives.length})`;
    loader.steps[0].progress = percent;
    store.dispatch(updateSmartLoader({ ...loader }));
  };

  // assets[i] MUST correspond to creatives[i]: downstream code attaches
  // dcmAssetMetadata by index (insertCreatives / insertCreativesNoAssignments /
  // insertDefaultCreative). The pool preserves that index alignment.
  let tasks = creatives.map((creative, i) => {
    let existing =
      creative.uploadedAssets && creative.uploadedAssets[advertiserId];

    if (existing) {
      // Uploaded in a previous, partially-failed submission — skip the request.
      loadedBytes[i] = totalBytes[i];
      return async () => {
        doneCount++;
        dispatchProgress();
        return existing;
      };
    }

    return async () => {
      try {
        let data = await uploadAssetWithRetry(
          creative,
          advertiserId,
          (loaded, total) => {
            loadedBytes[i] = loaded;
            if (total) totalBytes[i] = total;
            dispatchProgress();
          }
        );

        let metadata = Array.isArray(data) ? data[0] : data;

        // Persist the result so a later retry re-uploads only what failed.
        // Base the update on fresh store state so we don't clobber fields added
        // elsewhere (e.g. traffickedAdvertiserIds from the Preloader flow).
        let storeCreative =
          (store.getState().creatives.byId || {})[creative.id] || creative;
        store.dispatch(
          editCreative({
            ...storeCreative,
            uploadedAssets: {
              ...(storeCreative.uploadedAssets || {}),
              [advertiserId]: metadata
            }
          })
        );

        loadedBytes[i] = totalBytes[i];
        doneCount++;
        dispatchProgress();
        return metadata;
      } catch (error) {
        if (error.message === "UNAUTHORIZED") authFailed = true;
        throw error;
      }
    };
  });

  dispatchProgress();

  let results = await runPool(tasks, POOL_SIZE, () => authFailed);

  if (authFailed) throw new Error("UNAUTHORIZED");

  let failures = [];
  let assets = results.map((result, i) => {
    if (result && result.ok) return result.value;

    let name =
      (creatives[i].taxonomy && creatives[i].taxonomy.name) ||
      (creatives[i].file && creatives[i].file.name) ||
      `asset ${i + 1}`;
    failures.push({
      name,
      message:
        (result && result.error && result.error.message) || "Upload did not run"
    });
    return undefined;
  });

  if (failures.length) {
    throw new Error(
      `${failures.length} of ${creatives.length} assets failed to upload. ` +
        `Successfully uploaded assets are saved — submit again to retry only the failed files.\n` +
        failures.map((f) => `"${f.name}" — ${f.message}`).join("\n")
    );
  }

  loader.steps[0].inProgress = false;
  loader.steps[0].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  return assets;
};

const unassignPreviousAds = async (
  loader,
  loaderStep = 1,
  adInfo,
  placementIds
) => {
  loader.steps[loaderStep].inProgress = true;
  store.dispatch(updateSmartLoader({ ...loader }));
  let campaignStore = store.getState().dcmCampaigns;

  let headers = await makeAuthHeaders();

  headers["Content-Type"] = "application/json";

  let requestBody = {
    adInfo,
    placementIds
  };

  if (campaignStore.selectedId) {
    requestBody.campaignInfo = campaignStore.byId[campaignStore.selectedId];
    // let isoEndDate = new Date(requestBody.campaignInfo.endDate).toISOString();
    // requestBody.campaignInfo.endDate = isoEndDate;
  }

  let response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/dcm/creative/unassign`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(requestBody)
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("UNAUTHORIZED");
    }

    let body = await response.json();
    throw new Error(body.message);
  }

  loader.steps[1].inProgress = false;
  loader.steps[1].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));
};

const postNewCreatives = async (
  loader,
  step,
  apiCreatives,
  placementProps,
  type
) => {
  loader.steps[step].inProgress = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  let headers = await makeAuthHeaders();

  headers["Content-Type"] = "application/json";

  let response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/dcm/creative/insertNew`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        type,
        apiCreatives,
        placementProps
      })
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("UNAUTHORIZED");
    }

    let body = await response.json();
    throw new Error(body.message);
  }

  loader.steps[step].inProgress = false;
  loader.steps[step].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  let body = await response.json();

  return body;
};

const postNewCreativesOneAtATime = async (
  loader,
  step,
  apiCreatives,
  placementProps,
  type
) => {
  loader.steps[step].inProgress = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  let uploadedCreatives = [];
  let headers = await makeAuthHeaders();

  headers["Content-Type"] = "application/json";

  //initialize ignore list to prevent duplicate uploads of backup creatives
  let ignoreList = [];

  for (const creative of apiCreatives) {
    let backupCreative = null;
    let creativesToPush = [creative];

    if (ignoreList.includes(creative)) continue;

    // If the creative is H5 with a backup, bundle them in the same API call
    if (creative.originalFileName.includes(".zip") ||
        (creative.metadata.backupId && creative.metadata.creativeRole === "PRIMARY")) {
      backupCreative = apiCreatives.find((c) => c.metadata.id === creative.metadata.backupId)
      if (backupCreative) {
        ignoreList.push(backupCreative);
        creativesToPush.push(backupCreative);
      }
    }

    // If the creative has a companion (audio/video), bundle them in the same API call
    if (creative.metadata.companionId && creative.metadata.creativeRole === "PRIMARY") {
      let companionCreative = apiCreatives.find((c) => c.metadata.id === creative.metadata.companionId)
      if (companionCreative) {
        ignoreList.push(companionCreative);
        creativesToPush.push(companionCreative);
      }
    }

    let response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/dcm/creative/insertNew`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          type,
          apiCreatives: creativesToPush,
          placementProps
        })
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("UNAUTHORIZED");
      }

      let body = await response.json();
      throw new Error(body.message);
    }

    uploadedCreatives = uploadedCreatives.concat(await response.json());
  }

  loader.steps[step].inProgress = false;
  loader.steps[step].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  return uploadedCreatives;
};

const associateCreativesToCampaign = async (
  loader,
  loaderStep,
  apiCreatives,
  campaignId
) => {
  loader.steps[loaderStep].inProgress = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  let headers = await makeAuthHeaders();

  headers["Content-Type"] = "application/json";

  let response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/dcm/creative/associate`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        apiCreatives,
        campaignId
      })
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("UNAUTHORIZED");
    }

    let body = await response.json();
    throw new Error(body.message);
  }

  loader.steps[loaderStep].inProgress = false;
  loader.steps[loaderStep].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));
};

const insertAd = async (
  loader,
  loaderStep,
  type,
  adInfo,
  apiCreatives,
  placementIds,
  placementProps
) => {
  loader.steps[loaderStep].inProgress = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  let headers = await makeAuthHeaders();

  headers["Content-Type"] = "application/json";

  let response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/dcm/creative/ad`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        type,
        placementProps,
        apiCreatives,
        placementIds,
        adInfo
      })
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("UNAUTHORIZED");
    }

    let body = await response.json();
    throw new Error(body.message);
  }

  loader.steps[loaderStep].inProgress = false;
  loader.steps[loaderStep].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));
};

export const insertCreatives = async (creatives, assignedPlacements, type) => {
  let loader = _.defaultsDeep({}, initialLoaderNew);
  let uiCreativeOrder = creatives.map((c) => c.id);

  try {
    const creativesWithNewAssets = [];
    const creativesWithExistingAssets = [];

    // An existing DCM creative used as a COMPANION to a freshly-uploaded
    // primary must reach the backend's /insertNew payload (not the
    // existing-asset/ad-assignment path) so it can be found via companionId
    // and linked directly (see insertMediaCreativeWithCompanion in BartAPI,
    // which already supports linking an existing companion by ID with no
    // re-upload/no new asset). It's pulled into its own bucket — not
    // creativesWithNewAssets — because postAssets indexes assets[] against
    // creativesWithNewAssets by position and expects a real File to upload;
    // an existing companion has neither.
    const linkedExistingCompanionIds = new Set();
    for (const c of creatives) {
      if (c.file instanceof File && c.companionId) {
        linkedExistingCompanionIds.add(c.companionId);
      }
    }

    const linkedExistingCompanions = [];

    for (let i = 0; i < creatives.length; i++) {
      if (creatives[i].file instanceof File) {
        creativesWithNewAssets.push(creatives[i]);
      } else if (linkedExistingCompanionIds.has(creatives[i].id)) {
        linkedExistingCompanions.push(creatives[i]);
      } else {
        creativesWithExistingAssets.push(creatives[i]);
      }
    }

    const adInfo = makeAdInfo();
    const placementIds = assignedPlacements.map((placement) => placement.id);
    const placementProps = {
      advertiserId: assignedPlacements[0].advertiserId,
      campaignId: assignedPlacements[0].campaignId,
      compatibility: assignedPlacements[0].compatibility,
      size: assignedPlacements[0].size
    };

    const assets = await postAssets(
      loader,
      creativesWithNewAssets,
      placementProps.advertiserId
    );

    await unassignPreviousAds(loader, 1, adInfo, placementIds);

    let newApiCreatives = [];
    let existingApiCreatives = [];

    for (let i = 0; i < creativesWithNewAssets.length; i++) {
      let metadata = { ...creativesWithNewAssets[i] };

      let originalFileName = metadata.file.name;

      delete metadata.file;
      // Strip upload-resume bookkeeping so it never reaches the API payload.
      delete metadata.uploadedAssets;
      delete metadata.traffickedAdvertiserIds;

      // If this primary has a companion that's an existing DCM creative,
      // attach the DCM ID so the API can link it without creating a new one
      if (metadata.companionId) {
        let companion = creatives.find((c) => c.id === metadata.companionId);
        if (companion && companion.dcmId) {
          metadata.companionDcmId = companion.dcmId;
        }
      }

      newApiCreatives.push({
        metadata,
        originalFileName,
        dcmAssetMetadata: assets[i]
      });
    }

    for (let i = 0; i < linkedExistingCompanions.length; i++) {
      let metadata = { ...linkedExistingCompanions[i] };

      delete metadata.file;
      delete metadata.dcmCreative;
      // metadata.dcmId is intentionally kept — the backend's
      // insertMediaCreativeWithCompanion reads it to link this existing
      // creative as a companion instead of creating a new one.

      newApiCreatives.push({
        metadata,
        originalFileName: "N/A",
        dcmAssetMetadata: null
      });
    }

    for (let i = 0; i < creativesWithExistingAssets.length; i++) {
      let metadata = { ...creativesWithExistingAssets[i] };

      let dcmCreative = metadata.dcmCreative;

      delete metadata.file;
      delete metadata.dcmId;
      delete metadata.dcmCreative;

      if (type === "TRACKING") {
        if (dcmCreative) {
          existingApiCreatives.push({
            metadata,
            dcmCreative,
            originalFileName: "N/A"
          });
        } else {
          // A new tracking creative has no uploaded asset, so there is no entry
          // for it in `assets` (which is indexed by creativesWithNewAssets, a
          // different loop). Referencing assets[i] here used the wrong index
          // space; the backend's TRACKING_TEXT path ignores dcmAssetMetadata.
          newApiCreatives.push({
            metadata,
            originalFileName: "N/A",
            dcmAssetMetadata: null
          });
        }
      } else {
        existingApiCreatives.push({
          metadata,
          dcmCreative,
          originalFileName: "N/A"
        });
      }
    }

    let apiCreatives = await postNewCreatives(
      loader,
      2,
      newApiCreatives,
      placementProps,
      type
    );

    for (let i = 0; i < apiCreatives.length; i++) {
      delete apiCreatives[i].dcmAssetMetadata;
    }

    apiCreatives = reorderCreatives(
      apiCreatives.concat(existingApiCreatives),
      uiCreativeOrder
    );

    await associateCreativesToCampaign(
      loader,
      3,
      apiCreatives,
      placementProps.campaignId
    );
    await insertAd(
      loader,
      4,
      type,
      adInfo,
      apiCreatives,
      placementIds,
      placementProps
    );

    return apiCreatives;
  } catch (e) {
    console.log(e);
    store.dispatch(setCreativeApiError(e, true));
  } finally {
    setTimeout(() => {
      let newLoader = _.defaultsDeep({}, initialLoaderNew);
      newLoader.loading = false;
      newLoader.steps[0].inProgress = false;
      console.log("Reinitializing Loader");
      console.log(newLoader);
      store.dispatch(updateSmartLoader({ ...newLoader }));
    }, 1000);
  }
};

// Returns { sortedCreatives, exportNames } — or undefined if it caught an
// error. Writing the Bart export is the caller's job: the Preloader fires one
// of these per media type, and three concurrent XLSX.writeFile() calls to the
// same filename made the browser drop some of the downloads (QA saw 2 exports
// for 3 types). The caller collects exportNames and writes a single workbook.
export const insertCreativesNoAssignments = async (
  creatives,
  creativeOptions,
  type
) => {
  let loader = _.defaultsDeep({}, initialLoaderNoAssignment);

  try {
    let sortedCreatives = {};

    // Track which advertisers each creative has already been trafficked to. The
    // captured `creatives` references predate our dispatches, so we keep a local
    // accumulator instead of re-reading them; seeded from persisted state so a
    // resubmit after a partial failure skips already-completed advertisers.
    let traffickedByCreativeId = {};
    creatives.forEach((c) => {
      traffickedByCreativeId[c.id] = new Set(c.traffickedAdvertiserIds || []);
    });

    // inserts all creative assets into every advertiser the user has access to
    // and sorts them based on advertiser
    for (let advertiserId of creativeOptions.advertiserIds) {
      // Skip advertisers already fully trafficked (resume after a partial
      // failure) so we don't insert duplicate creatives.
      if (
        creatives.every((c) => traffickedByCreativeId[c.id].has(advertiserId))
      ) {
        continue;
      }

      loader = _.defaultsDeep({}, initialLoaderNoAssignment); // reset loader on each batch to prevent errors
      let assets = await postAssets(loader, creatives, advertiserId);

      let newApiCreatives = [];

      for (let i = 0; i < creatives.length; i++) {
        let metadata = { ...creatives[i] };

        let originalFileName = metadata.file.name;

        delete metadata.file;
        // Strip upload-resume bookkeeping so it never reaches the API payload.
        delete metadata.uploadedAssets;
        delete metadata.traffickedAdvertiserIds;

        newApiCreatives.push({
          metadata,
          originalFileName,
          dcmAssetMetadata: assets[i],
          createdInPreloader: creatives[i].createdInPreloader
        });
      }

      let compatibility = "DISPLAY";
      if (type === "INSTREAM_AUDIO") compatibility = "IN_STREAM_AUDIO";
      if (type === "INSTREAM_VIDEO") compatibility = "IN_STREAM_VIDEO";

      let apiCreatives = await postNewCreativesOneAtATime(loader, 1, newApiCreatives, {
        advertiserId,
        compatibility
      });

      for (let i = 0; i < apiCreatives.length; i++) {
        delete apiCreatives[i].dcmAssetMetadata;
      }

      sortedCreatives[advertiserId] = apiCreatives;

      // Mark this advertiser complete for each creative so a resubmit after a
      // later advertiser fails won't re-insert duplicates here. Base each update
      // on fresh store state to preserve fields added by postAssets.
      creatives.forEach((c) => {
        traffickedByCreativeId[c.id].add(advertiserId);
        let storeCreative = (store.getState().creatives.byId || {})[c.id] || c;
        store.dispatch(
          editCreative({
            ...storeCreative,
            traffickedAdvertiserIds: Array.from(traffickedByCreativeId[c.id])
          })
        );
      });
    }

    return {
      sortedCreatives,
      exportNames: creatives
        .filter((c) => c.creativeRole === "PRIMARY")
        .map(getExportName)
        .filter(Boolean),
      error: null
    };
  } catch (e) {
    console.log(e);
    // Return the failure rather than dispatching it. The Preloader runs one of
    // these per media type concurrently, and they all wrote the same shared
    // apiError slot — so one failed type surfaced as a single generic error on
    // the Confirmation screen, making a mostly-successful submission look like
    // a total failure with no indication of which type actually broke. The
    // caller aggregates these and dispatches one attributed error.
    return { sortedCreatives: {}, exportNames: [], error: e };
  } finally {
    setTimeout(() => {
      let newLoader = _.defaultsDeep({}, initialLoaderNoAssignment);
      newLoader.loading = false;
      newLoader.steps[0].inProgress = false;
      console.log("Reinitializing Loader");
      console.log(newLoader);
      store.dispatch(updateSmartLoader({ ...newLoader }));
    }, 1000);
  }
};

export const assignCreatives = async (creatives, assignedPlacements) => {
  let loader = _.defaultsDeep({}, initialLoaderExisting);

  for (let i = 0; i < creatives.length; i++) {
    creatives[i].metadata = {
      taxonomy: creatives[i].taxonomy,
      weight: creatives[i].weight
    };

    delete creatives[i].taxonomy;
  }

  let adInfo = makeAdInfo();
  const placementIds = assignedPlacements.map((placement) => placement.id);
  const placementProps = {
    advertiserId: assignedPlacements[0].advertiserId,
    campaignId: assignedPlacements[0].campaignId,
    compatibility: assignedPlacements[0].compatibility,
    size: assignedPlacements[0].size
  };

  try {
    await associateCreativesToCampaign(
      loader,
      0,
      creatives,
      placementProps.campaignId
    );
    await unassignPreviousAds(loader, 1, adInfo, placementIds);

    await insertAd(
      loader,
      2,
      "DISPLAY",
      adInfo,
      creatives,
      placementIds,
      placementProps
    );
  } catch (e) {
    console.log(e);
    store.dispatch(setCreativeApiError(e, true));
  } finally {
    setTimeout(() => {
      loader = _.defaultsDeep({}, initialLoaderExisting);
      loader.loading = false;
      store.dispatch(updateSmartLoader({ ...loader }));
    }, 1000);
  }
};

export const fetchCreativesByIds = async (creativeIds) => {
  if (!creativeIds || creativeIds.length === 0) return [];

  try {
    let headers = await makeAuthHeaders();

    // Batch into chunks of 50 to avoid URL length limits and GCM API constraints
    const BATCH_SIZE = 50;
    const allResults = [];

    for (let i = 0; i < creativeIds.length; i += BATCH_SIZE) {
      const batch = creativeIds.slice(i, i + BATCH_SIZE);
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/dcm/creatives?creativeIds=${batch.toString()}`,
        {
          method: "GET",
          headers
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("UNAUTHORIZED");
        }

        let body = await response.json();
        throw new Error(body.message);
      }

      const data = await response.json();
      if (data && data.length > 0) allResults.push(...data);
    }

    return allResults;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

const buildCreativeQueryString = (creativeTypes, sizes, advertiserId, pageToken) => {
  let queryParams = [];

  if (creativeTypes.length > 0) {
    queryParams.push(`creativeTypes=${creativeTypes.join(",")}`);
  }

  if (sizes.length > 0) {
    queryParams.push(`sizes=${sizes.join(",")}`);
  }

  if (advertiserId.length > 0) {
    queryParams.push(`advertiserId=${advertiserId}`);
  }

  if (pageToken) {
    queryParams.push(`pageToken=${pageToken}`);
  }

  let queryString = queryParams.join("&");
  return queryString.length > 0 ? `?${queryString}` : "";
};

export const fetchAssignedCreativesService = async (
  campaignId,
  creativeTypes = [],
  sizes = [],
  advertiserId = "",
  pageToken = null,
  signal = undefined
) => {
  let queryString = buildCreativeQueryString(creativeTypes, sizes, advertiserId, pageToken);

  try {
    let headers = await makeAuthHeaders();

    let response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/dcm/${campaignId}/creatives${queryString}`,
      {
        method: "GET",
        headers,
        signal
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("UNAUTHORIZED");
      }

      let body = await response.json();
      throw new Error(body.message);
    }

    return response.json();
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const insertRichMediaTemplates = async (advertisers, creatives) => {
  try {
    let headers = await makeAuthHeaders();

    headers["Content-Type"] = "application/json";

    let requestBody = {};
    let dcmSizes = {};

    for (let advertiser of advertisers) {
      for (let creative of creatives) {
        if (!dcmSizes[creative.taxonomy.dimensions]) {
          let parsedSizeArray = creative.taxonomy.dimensions.split("x");
          let parsedSize = {
            width: parsedSizeArray[0].trim(),
            height: parsedSizeArray[1].trim()
          };

          let dcmSize = await fetch(
            `${process.env.REACT_APP_API_URL}/api/dcm/size?width=${parsedSize.width}&height=${parsedSize.height}`,
            {
              method: "GET",
              headers
            }
          );

          dcmSizes[creative.taxonomy.dimensions] = await dcmSize.json();
        }

        requestBody = {
          creative: { ...creative, metadata: { taxonomy: creative.taxonomy } },
          metadata: { advertiser, size: dcmSizes[creative.taxonomy.dimensions] }
        };

        console.log(JSON.stringify(requestBody));

        let dcmCreative = await fetch(
          `${process.env.REACT_APP_API_URL}/api/dcm/creative/insert`,
          { method: "POST", headers, body: JSON.stringify(requestBody) }
        );

        console.log(await dcmCreative.json());
      }
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const insertDefaultCreative = async (
  creatives,
  campaign,
  dimensions,
  type
) => {
  let loader = _.defaultsDeep({}, initialLoaderDefaultDimensions);
  let uiCreativeOrder = creatives.map((c) => c.id);

  try {
    const creativesWithNewAssets = [];
    const creativesWithExistingAssets = [];

    for (let i = 0; i < creatives.length; i++) {
      if (creatives[i].file instanceof File) {
        creativesWithNewAssets.push(creatives[i]);
      } else {
        creativesWithExistingAssets.push(creatives[i]);
      }
    }

    dimensions = dimensions.split("x"); //width is [0] height is [1]

    let dcmSize = await fetchSizeService(dimensions[0], dimensions[1]);
    let computedCompatibility = "DISPLAY";

    if (type === "INSTREAM_AUDIO") {
      computedCompatibility = "IN_STREAM_AUDIO";
    } else if ((dimensions[0] === "0") & (dimensions[1] === "0")) {
      computedCompatibility = "IN_STREAM_VIDEO";
    }
    // const adInfo = makeAdInfo();
    // const placementIds = assignedPlacements.map((placement) => placement.id);
    const placementProps = {
      advertiserId: campaign.advertiserId,
      campaignId: campaign.id,
      compatibility: computedCompatibility, // need to change to allow video as well
      size: dcmSize
    };

    const assets = await postAssets(
      loader,
      creativesWithNewAssets,
      placementProps.advertiserId
    );

    let newApiCreatives = [];
    let existingApiCreatives = [];

    for (let i = 0; i < creativesWithNewAssets.length; i++) {
      let metadata = { ...creativesWithNewAssets[i] };

      let originalFileName = metadata.file.name;

      delete metadata.file;
      // Strip upload-resume bookkeeping so it never reaches the API payload.
      delete metadata.uploadedAssets;
      delete metadata.traffickedAdvertiserIds;

      newApiCreatives.push({
        metadata,
        originalFileName,
        dcmAssetMetadata: assets[i]
      });
    }

    for (let i = 0; i < creativesWithExistingAssets.length; i++) {
      let metadata = { ...creativesWithExistingAssets[i] };

      let dcmCreative = metadata.dcmCreative;

      delete metadata.file;
      delete metadata.dcmId;
      delete metadata.dcmCreative;

      if (type === "TRACKING") {
        if (dcmCreative) {
          existingApiCreatives.push({
            metadata,
            dcmCreative,
            originalFileName: "N/A"
          });
        } else {
          // A new tracking creative has no uploaded asset, so there is no entry
          // for it in `assets` (which is indexed by creativesWithNewAssets, a
          // different loop). Referencing assets[i] here used the wrong index
          // space; the backend's TRACKING_TEXT path ignores dcmAssetMetadata.
          newApiCreatives.push({
            metadata,
            originalFileName: "N/A",
            dcmAssetMetadata: null
          });
        }
      } else {
        existingApiCreatives.push({
          metadata,
          dcmCreative,
          originalFileName: "N/A"
        });
      }
    }

    let apiCreatives = await postNewCreatives(
      loader,
      1,
      newApiCreatives,
      placementProps,
      type
    );

    for (let i = 0; i < apiCreatives.length; i++) {
      delete apiCreatives[i].dcmAssetMetadata;
    }

    apiCreatives = reorderCreatives(
      apiCreatives.concat(existingApiCreatives),
      uiCreativeOrder
    );

    await associateCreativesToCampaign(
      loader,
      2,
      apiCreatives,
      placementProps.campaignId
    );
    // await insertAd(
    //   loader,
    //   4,
    //   type,
    //   adInfo,
    //   apiCreatives
    //   // placementIds,
    //   // placementProps
    // );

    return apiCreatives;
  } catch (e) {
    console.log(e);
    store.dispatch(setCreativeApiError(e, true));
  } finally {
    setTimeout(() => {
      let newLoader = _.defaultsDeep({}, initialLoaderDefaultDimensions);
      newLoader.loading = false;
      newLoader.steps[0].inProgress = false;
      console.log("Reinitializing Loader");
      console.log(newLoader);
      store.dispatch(updateSmartLoader({ ...newLoader }));
    }, 1000);
  }
};